import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown } from 'lucide-react'
import {
  CompanyRegistryEvent,
  CVROwnershipStructure,
  CVRParticipant,
} from '../../types'
import { Section, Badge, CountryFlagIcon } from '../ui'
import EntityProfileLink from '../watchlist/EntityProfileLink'
import WatchlistIndicator from '../watchlist/WatchlistIndicator'
import { participantWatchlistEntity } from '../../utils/watchlistEntity'
import {
  formatDate,
  formatLegalRegisterPercent,
  formatPercentRange,
  participantCountryFlagCode,
  roleChangePhrase,
} from '../../utils/format'
import {
  activityRowYear,
  collectAllCompanyActivity,
  collectRecentCompanyActivity,
  CompanyActivityRow,
} from '../../utils/companyActivity'
import {
  ActivityTypeToggle,
  ACTIVITY_TYPE_LABELS,
  availableActivityTypes,
  filterActivityRows,
  registryEventDescription,
} from '../../utils/registryActivity'

const TOOLBAR_CONTROL =
  'h-7 box-border border border-rule rounded-sm text-xs leading-none'

/**
 * Role transitions and company registry events (navn, adresse, form, status).
 *
 * `scope="recent"` — last 12 months only; cap at 5 visible behind a toggle.
 * `scope="all"` — full history (Aktivitet tab); year breaker headings + search/type filters.
 */
interface RecentChangesProps {
  ownership: CVROwnershipStructure
  registryEvents?: CompanyRegistryEvent[]
  /** Override the "now" reference — used by tests for deterministic output. */
  now?: Date
  /** `recent` = 12-month window; `all` = every registered transition. */
  scope?: 'recent' | 'all'
  /**
   * How many rows to show before collapsing the rest behind a toggle.
   * Default `5` for `scope="recent"`; `Infinity` for `scope="all"`.
   */
  initialVisible?: number
}

const BUCKET_LABEL: Record<keyof CVROwnershipStructure, string> = {
  beneficialOwners: 'reel ejer',
  ownershipRegister: 'legal ejer',
  management: 'direktion',
  boardMembers: 'bestyrelse',
  auditors: 'revision',
  other: 'anden rolle',
}

const WINDOW_DAYS = 366
const DEFAULT_INITIAL_VISIBLE = 5

const RecentChanges: React.FC<RecentChangesProps> = ({
  ownership,
  registryEvents = [],
  now,
  scope = 'recent',
  initialVisible,
}) => {
  const reference = now ?? new Date()
  const activity = useMemo(
    () =>
      scope === 'all'
        ? collectAllCompanyActivity(ownership, registryEvents, reference)
        : collectRecentCompanyActivity(ownership, registryEvents, reference, WINDOW_DAYS),
    [ownership, registryEvents, reference, scope]
  )
  const [expanded, setExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTypes, setActiveTypes] = useState<Set<ActivityTypeToggle>>(
    () => new Set()
  )

  const typeOptions = useMemo(() => availableActivityTypes(activity), [activity])
  const filteredActivity = useMemo(
    () => filterActivityRows(activity, activeTypes, searchQuery),
    [activity, activeTypes, searchQuery]
  )

  const defaultCap = scope === 'all' ? Infinity : DEFAULT_INITIAL_VISIBLE
  const cap = Number.isFinite(initialVisible ?? defaultCap)
    ? (initialVisible ?? defaultCap)
    : filteredActivity.length
  const visible = expanded ? filteredActivity : filteredActivity.slice(0, cap)
  const overflow = filteredActivity.length - cap
  const showYearHeaders = scope === 'all'
  const showToolbar = scope === 'all' && activity.length > 0

  const toggleActiveType = (id: ActivityTypeToggle) => {
    setActiveTypes((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <Section
      title={scope === 'all' ? 'Aktivitet' : 'Seneste aktivitet'}
      emphasis
      compact
      trailing={
        showToolbar ? (
          <ActivityToolbar
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            typeOptions={typeOptions}
            activeTypes={activeTypes}
            onToggleType={toggleActiveType}
          />
        ) : undefined
      }
    >
      {filteredActivity.length === 0 ? (
        <p className="text-xs text-ink-500">
          {activity.length === 0
            ? scope === 'all'
              ? 'Ingen registreret aktivitet.'
              : 'Ingen registreret aktivitet i den seneste 12-måneders periode.'
            : 'Ingen aktivitet matcher filteret.'}
        </p>
      ) : (
        <>
          <ol className="list-none">
            {visible.map((row, i) => {
              const year = activityRowYear(row)
              const prevYear =
                i > 0 ? activityRowYear(visible[i - 1]) : null
              const showYear = showYearHeaders && year !== prevYear
              const rowKey =
                row.source === 'role'
                  ? `role-${row.bucket}-${row.kind}-${row.date}-${row.participant.enhedsNummer ?? i}`
                  : `registry-${row.event.kind}-${row.event.date}-${i}`
              return (
                <React.Fragment key={rowKey}>
                  {showYear && <YearBreaker year={year} />}
                  {row.source === 'role' ? (
                    <RoleChangeListItem row={row} />
                  ) : (
                    <RegistryEventListItem event={row.event} />
                  )}
                </React.Fragment>
              )
            })}
          </ol>
          {overflow > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-sm text-ink-700 hover:text-ink underline underline-offset-4 decoration-rule-strong hover:decoration-signal"
              aria-expanded={expanded}
            >
              {expanded ? 'Skjul' : `Vis al aktivitet (${filteredActivity.length})`}
            </button>
          )}
        </>
      )}
    </Section>
  )
}

const ActivityToolbar: React.FC<{
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  typeOptions: ActivityTypeToggle[]
  activeTypes: ReadonlySet<ActivityTypeToggle>
  onToggleType: (id: ActivityTypeToggle) => void
}> = ({
  searchQuery,
  onSearchQueryChange,
  typeOptions,
  activeTypes,
  onToggleType,
}) => (
  <div className="flex items-center gap-1.5">
    <label className="block h-7 shrink-0">
      <span className="sr-only">Søg i aktivitet</span>
      <input
        type="search"
        value={searchQuery}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        placeholder="Søg…"
        className={`${TOOLBAR_CONTROL} w-[9.5rem] sm:w-[11rem] appearance-none px-2 bg-paper focus:outline-none focus:ring-1 focus:ring-signal placeholder:text-ink-400`}
      />
    </label>
    {typeOptions.length > 0 && (
      <ActivityTypeDropdown
        typeOptions={typeOptions}
        activeTypes={activeTypes}
        onToggleType={onToggleType}
      />
    )}
  </div>
)

const ActivityTypeDropdown: React.FC<{
  typeOptions: ActivityTypeToggle[]
  activeTypes: ReadonlySet<ActivityTypeToggle>
  onToggleType: (id: ActivityTypeToggle) => void
}> = ({ typeOptions, activeTypes, onToggleType }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerLabel =
    activeTypes.size > 0 ? `Typer (${activeTypes.size})` : 'Typer'

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Filtrer aktivitetstyper"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex ${TOOLBAR_CONTROL} items-center gap-1 px-2 text-ink-600 hover:text-ink hover:border-ink-300 bg-paper transition-colors`}
      >
        <span>{triggerLabel}</span>
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Filtrer aktivitetstyper"
          className="absolute right-0 top-full z-30 mt-1 min-w-[12rem] max-h-[min(16rem,calc(100vh-1.5rem))] overflow-y-auto bg-paper border border-rule shadow-lg rounded-sm py-1"
        >
          {typeOptions.map((id) => {
            const selected = activeTypes.has(id)
            return (
              <button
                key={id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={selected}
                onClick={() => onToggleType(id)}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-ink hover:bg-paper-200 transition-colors"
              >
                <span aria-hidden className="w-3 shrink-0 text-[10px] text-ink-500">
                  {selected ? '✓' : ''}
                </span>
                <span className={selected ? 'font-semibold' : ''}>
                  {ACTIVITY_TYPE_LABELS[id]}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const YearBreaker: React.FC<{ year: string }> = ({ year }) => (
  <li
    aria-hidden
    className="pt-4 pb-1 first:pt-0 border-t border-rule first:border-t-0"
  >
    <span className="text-[13px] font-semibold text-ink-500">
      {year}
    </span>
  </li>
)

const RoleChangeListItem: React.FC<{
  row: Extract<CompanyActivityRow, { source: 'role' }>
}> = ({ row }) => {
  const { kind, date, bucket, participant } = row
  const dateLabel = formatDate(date)
  const stake =
    bucket === 'beneficialOwners'
      ? formatPercentRange(
          participant.role.ownershipCapitalPercent,
          participant.role.ownershipCapitalPercentMax
        )
      : bucket === 'ownershipRegister'
        ? formatLegalRegisterPercent(
            participant.role.ownershipCapitalPercent,
            participant.role.ownershipCapitalPercentMax,
            participant.role.ownershipRegistrationUnderFive
          )
        : null
  const Icon = kind === 'role_in' ? ArrowDown : ArrowUp

  return (
    <li className="py-1.5 border-b border-rule last:border-b-0 text-sm">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="tabular-nums text-xs text-ink-500 shrink-0 w-28">
          {dateLabel}
        </span>
        <span
          title={kind === 'role_in' ? 'Indtrådt' : 'Udtrådt'}
          aria-label={kind === 'role_in' ? 'Indtrådt' : 'Udtrådt'}
          className="inline-flex shrink-0 mt-0.5 p-1 -m-1 cursor-default"
        >
          <Icon
            aria-hidden
            className="h-4 w-4 text-ink-500"
          />
        </span>
        <span className="flex-1 min-w-[12rem] text-ink">
          <ParticipantName participant={participant} />{' '}
          <span className="text-ink-700">
            {roleChangePhrase(kind === 'role_in' ? 'in' : 'out', BUCKET_LABEL[bucket])}
          </span>
          {stake && (
            <Badge tone="muted" className="ml-2">
              {stake}
            </Badge>
          )}
        </span>
      </div>
    </li>
  )
}

const RegistryEventListItem: React.FC<{ event: CompanyRegistryEvent }> = ({ event }) => {
  const dateLabel = formatDate(event.date)

  return (
    <li className="py-1.5 border-b border-rule last:border-b-0 text-sm">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="tabular-nums text-xs text-ink-500 shrink-0 w-28">
          {dateLabel}
        </span>
        <span aria-hidden className="inline-flex shrink-0 w-4 h-4 mt-0.5" />
        <span className="flex-1 min-w-[12rem] text-ink-700">
          {registryEventDescription(event)}
        </span>
      </div>
    </li>
  )
}

const ParticipantName: React.FC<{ participant: CVRParticipant }> = ({ participant }) => {
  const name = participant.navn || 'Ukendt'
  const flagCode = participantCountryFlagCode(participant)
  const nameContent = (
    <>
      {name}
      {flagCode && <CountryFlagIcon code={flagCode} />}
    </>
  )
  const entity = participantWatchlistEntity(participant)
  if (!participant.enhedsNummer) {
    return (
      <span className="inline-flex items-baseline gap-1 font-medium">
        {nameContent}
        {entity && (
          <WatchlistIndicator
            entityType={entity.entityType}
            entityId={entity.entityId}
          />
        )}
      </span>
    )
  }
  if (participant.enhedstype === 'VIRKSOMHED') {
    if (!participant.cvrNummer) {
      return (
        <span className="inline-flex items-baseline gap-1 font-medium">
          {nameContent}
          {entity && (
            <WatchlistIndicator
              entityType={entity.entityType}
              entityId={entity.entityId}
            />
          )}
        </span>
      )
    }
    return (
      <EntityProfileLink
        to={`/company/${participant.cvrNummer}`}
        entityType="company"
        entityId={String(participant.cvrNummer)}
        className="inline-flex items-baseline gap-1 font-medium hover:underline decoration-signal underline-offset-2"
      >
        {nameContent}
      </EntityProfileLink>
    )
  }
  return (
    <EntityProfileLink
      to={`/person/${participant.enhedsNummer}`}
      entityType="person"
      entityId={String(participant.enhedsNummer)}
      className="inline-flex items-baseline gap-1 font-medium hover:underline decoration-signal underline-offset-2"
    >
      {nameContent}
    </EntityProfileLink>
  )
}

export {
  collectRecentChanges,
  collectAllChanges,
  changeYear,
  collectAllCompanyActivity,
} from '../../utils/companyActivity'

export default RecentChanges
